import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CourtsRepository } from '../api/courts-repository'
import { assertPresent } from '@/shared/utils/assert'
import { useClub } from '@/features/settings/hooks/use-club'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { effectiveCourtCapacity, type PricingRule } from '@/core/models/court'

export const courtsKeys = {
  all: (clubId: string) => ['courts', clubId, 'all'] as const,
  active: (clubId: string) => ['courts', clubId, 'active'] as const,
}

export function useCourts(clubId: string | null, options: { activeOnly?: boolean } = {}) {
  const repo = useMemo(() => (clubId ? new CourtsRepository(clubId) : null), [clubId])
  const activeOnly = options.activeOnly ?? false

  return useQuery({
    queryKey: clubId
      ? activeOnly
        ? courtsKeys.active(clubId)
        : courtsKeys.all(clubId)
      : ['courts', 'none'],
    queryFn: () => {
      const r = assertPresent(repo, 'repo')
      return activeOnly ? r.listActive() : r.listAll()
    },
    enabled: !!repo,
    staleTime: 60_000,
  })
}

/**
 * Active-court count. Single read for the cost-driving number — the same
 * value drives Stripe billing quantity, the picker chip, factura line
 * items, and per-court analytics.
 *
 * Prefers the live `useCourts({ activeOnly: true })` count when loaded;
 * falls back to the cached `clubs/{id}.activeCourtCount` when the courts
 * subcollection hasn't been queried yet (avoids the brief 0 flash on
 * pages that don't render any court list themselves).
 *
 * Both numbers are kept in sync server-side by the `on_court_written`
 * Firestore trigger — by construction they cannot diverge except during
 * the milliseconds between a court write and the trigger completing.
 */
export function useActiveCourtCount(clubId: string | null): number {
  const courtsQuery = useCourts(clubId, { activeOnly: true })
  const clubQuery = useClub(clubId)
  return courtsQuery.data?.length ?? clubQuery.data?.activeCourtCount ?? 0
}

/**
 * The club's default base pricing rule (priority 0, else first). A court
 * with no own pricing rules still charges this rule at booking time
 * (`priceFor()` falls back to it), so the Pistas list shows it as the
 * effective "del club" price instead of a misleading "sin precio".
 *
 * Lives here (feature-hooks) rather than in the page so the cross-feature
 * `useClub` import stays within the allowed dependency boundary.
 */
export function useClubBaseRule(clubId: string | null): PricingRule | undefined {
  const clubQuery = useClub(clubId)
  return useMemo(() => {
    const rules = clubQuery.data?.defaultPricingRules ?? []
    return rules.find((r) => r.priority === 0) ?? rules[0]
  }, [clubQuery.data?.defaultPricingRules])
}

/**
 * All club-level default pricing rules (includes peak/off-peak).
 * Used by the Pistas price cell to display the full price range
 * (min–max across all rules) for courts that inherit club pricing.
 * Returns a stable empty array while the club query is loading.
 */
export function useClubDefaultRules(clubId: string | null): PricingRule[] {
  const clubQuery = useClub(clubId)
  return useMemo(
    () => clubQuery.data?.defaultPricingRules ?? [],
    [clubQuery.data?.defaultPricingRules],
  )
}

/**
 * Resolve the player capacity for a single court. Returns the court's
 * explicit `capacity` when set; else the size-derived default (4 for
 * normal, 2 for small). Returns 4 while the courts query is loading or
 * when the courtId isn't found, so downstream UI doesn't flash 0 dots.
 *
 * Uses `effectiveCourtCapacity` so an operator's explicit override always
 * wins over the size default — critical for per-player split correctness
 * in Cobrar and roster-cap validation.
 *
 * TanStack Query dedupes the underlying `useCourts` query, so calling
 * this hook from many sibling components (e.g. one per booking row or
 * tile) doesn't multiply the network cost.
 */
export function useCourtCapacity(courtId: string | null | undefined): number {
  const clubId = useCurrentClubStore((s) => s.clubId)
  const courtsQuery = useCourts(clubId, { activeOnly: false })
  if (!courtId) return 4
  const court = courtsQuery.data?.find((c) => c.id === courtId)
  return court ? effectiveCourtCapacity(court) : 4
}
